import { CalendarDays, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'

const QUOTE_PERIOD_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'previous_month', label: 'Previous Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'previous_quarter', label: 'Previous Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Date Range' },
]

export default function QuoteFilters({
  total = 0,
  filtered = 0,
  period = 'all',
  onPeriodChange,
  startDate = '',
  endDate = '',
  onStartDateChange,
  onEndDateChange,
  onApplyCustomDate,
  salesPersons = [],
  salesPerson = 'all',
  onSalesPersonChange,
}) {
  const [isSalesOpen, setIsSalesOpen] = useState(false)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)

  const selectedPeriodLabel =
    QUOTE_PERIOD_OPTIONS.find((option) => option.value === period)?.label ||
    'All Time'

  const selectedSalesPerson = salesPersons.find(
    (person) => String(person._id || person.id) === String(salesPerson),
  )

  const selectedSalesPersonLabel =
    salesPerson === 'all'
      ? 'All Sales Persons'
      : selectedSalesPerson?.name || 'Sales Person'

  return (
    <div className="flex w-full items-center justify-end gap-3">
      {/* Result count */}
      <span className="shrink-0 text-sm text-slate-600">
        {filtered} of {total}
      </span>

      {/* Sales Person */}
      <DropdownMenu open={isSalesOpen} onOpenChange={setIsSalesOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="h-10 min-w-[150px] gap-2 rounded-xl px-4 font-medium"
          >
            <span className="truncate">{selectedSalesPersonLabel}</span>

            {isSalesOpen ? (
              <ChevronUp className="size-4 shrink-0" />
            ) : (
              <ChevronDown className="size-4 shrink-0" />
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-[200px]">
          <DropdownMenuItem onSelect={() => onSalesPersonChange?.('all')}>
            All Sales Persons
          </DropdownMenuItem>

          {salesPersons.map((person) => {
            const id = person._id || person.id

            return (
              <DropdownMenuItem
                key={id}
                onSelect={() => onSalesPersonChange?.(String(id))}
              >
                {person.name || person.email}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Period */}
      <DropdownMenu open={isPeriodOpen} onOpenChange={setIsPeriodOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="h-10 min-w-[145px] gap-2 rounded-xl px-4 font-medium"
          >
            {selectedPeriodLabel}
            {isPeriodOpen ? (
              <ChevronUp className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-60">
          {QUOTE_PERIOD_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => onPeriodChange?.(option.value)}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Custom dates */}
      {period === 'custom' && (
        <>
          <div className="relative">
            <CalendarDays className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />

            <input
              type="date"
              value={startDate}
              onChange={(event) => onStartDateChange?.(event.target.value)}
              className="h-10 w-[145px] rounded-xl border bg-white pl-9 pr-3 text-sm outline-none"
            />
          </div>

          <div className="relative">
            <CalendarDays className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />

            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(event) => onEndDateChange?.(event.target.value)}
              className="h-10 w-[145px] rounded-xl border bg-white pl-9 pr-3 text-sm outline-none"
            />
          </div>

          <Button
            type="button"
            className="h-10 rounded-xl px-5 bg-[#0F172A]"
            disabled={!startDate || !endDate}
            onClick={onApplyCustomDate}
          >
            Apply
          </Button>
        </>
      )}
    </div>
  )
}
